import { Link } from '@tanstack/react-router';
import { Info, Loader2, Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { type EmailToggle } from '~entities/settings/email-settings/api/get-email-toggles';
import { useUpdateEmailToggle } from '~entities/settings/email-settings/api/update-email-toggle';

import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Switch } from '~shared/shadcn/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

import { EmailPreviewDialog } from './email-preview-dialog';
import { EmailTemplateEditorDialog } from './email-template-editor-dialog';
import { SYSTEM_EMAIL_TEMPLATE_EDITOR_ENABLED } from './system-email-template-editor-enabled';

interface EmailToggleCardProps {
  toggle: EmailToggle;
  disabled?: boolean;
}

const SCENARIO_LABELS: Record<string, string> = {
  TEMPORARY_PASSWORD: 'Welcome email (new families)',
  PAYMENT_RECEIPT: 'Payment Receipt',
  TRANSACTION: 'Transaction Receipt',
  POLICIES: 'Studio Policies',
  FAILED_PAYMENTS_SUMMARY: 'Failed Payments Summary',
  WAITLIST_CONFIRMATION: 'Waitlist Confirmation',
  ABSENCE_MARKED: 'Absence Marked',
  CONSECUTIVE_ABSENCES_REACHED: 'Consecutive Absences Reached',
  PRIVATE_LESSON_REMINDER: 'Private Lesson Reminder email',
  MEMBERSHIP_FEE_REMINDER: 'Fees Owed Reminder email',
};

const SCENARIO_DESCRIPTIONS: Record<string, string> = {
  TEMPORARY_PASSWORD:
    'Sent when a family is invited to the parent portal with a temporary password.',
  PAYMENT_RECEIPT: 'Sent to parents after a successful payment.',
  TRANSACTION: 'Invoice / transaction emails sent to parents.',
  POLICIES: 'Studio policies and enrollment confirmation emails.',
  WAITLIST_CONFIRMATION:
    'Confirmation emails sent to parents when a student joins a class waitlist',
  PRIVATE_LESSON_REMINDER:
    'Enable or customize the reminder email template. When reminders are scheduled and who receives them is set under Reminders.',
  MEMBERSHIP_FEE_REMINDER:
    'Weekly digest of families that still need a membership fee charged. Turn the reminder on under Reminders settings.',
};

export const EmailToggleCard = ({
  toggle,
  disabled = false,
}: EmailToggleCardProps) => {
  const { mutateAsync: updateToggle, isPending } = useUpdateEmailToggle();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  const emailType = toggle.emailType || '';
  const isWelcomeEmail = emailType === 'TEMPORARY_PASSWORD';
  // ENR-1641: hide Edit on production until the editor is reinvented.
  const canCustomizeEmail =
    SYSTEM_EMAIL_TEMPLATE_EDITOR_ENABLED &&
    Boolean(emailType && emailType !== 'WAITLIST_JOINED');
  const title = SCENARIO_LABELS[emailType] ?? toggle.scenarioName;
  const description = SCENARIO_DESCRIPTIONS[emailType] ?? toggle.description;

  const handleToggleChange = async (checked: boolean) => {
    try {
      await updateToggle({ id: toggle.id, disable: !checked });
      toast.success(`Email notification ${!checked ? 'disabled' : 'enabled'}`);
    } catch (error) {
      toast.error('Failed to update email notification');
      console.error(error);
    }
  };

  return (
    <>
      <Card className='w-full'>
        <CardHeader className='pb-2'>
          <div className='flex items-center justify-between gap-2'>
            <div className='flex min-w-0 items-center gap-1.5'>
              <CardTitle className='truncate text-lg'>{title}</CardTitle>
              {toggle.hasCustomTemplate && (
                <Badge variant='secondary' className='shrink-0'>
                  Custom
                </Badge>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='size-6 shrink-0'
                    onClick={() => {
                      setPreviewOpen(true);
                    }}
                  >
                    <Info className='size-4 text-muted-foreground' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Preview email</TooltipContent>
              </Tooltip>
            </div>
            <div className='flex shrink-0 items-center gap-2'>
              {canCustomizeEmail ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {isWelcomeEmail ? (
                      <Button
                        type='button'
                        variant='outline'
                        size='icon'
                        className='size-8'
                        asChild
                      >
                        <Link to='/email-customizer' aria-label='Edit'>
                          <Pencil className='size-3.5' />
                        </Link>
                      </Button>
                    ) : (
                      <Button
                        type='button'
                        variant='outline'
                        size='icon'
                        className='size-8'
                        onClick={() => {
                          setEditorOpen(true);
                        }}
                        aria-label='Edit'
                      >
                        <Pencil className='size-3.5' />
                      </Button>
                    )}
                  </TooltipTrigger>
                  <TooltipContent>Edit</TooltipContent>
                </Tooltip>
              ) : null}
              <div className='relative'>
                {isPending && (
                  <div className='absolute -left-7 top-0 flex items-center'>
                    <Loader2 className='size-4 animate-spin' />
                  </div>
                )}
                <Switch
                  checked={!toggle.disabled}
                  onCheckedChange={handleToggleChange}
                  disabled={isPending || disabled}
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <CardDescription>{description}</CardDescription>
        </CardContent>
      </Card>

      <EmailPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        scenarioId={toggle.id}
        scenarioName={title}
      />

      {!isWelcomeEmail && canCustomizeEmail ? (
        <EmailTemplateEditorDialog
          open={editorOpen}
          onOpenChange={setEditorOpen}
          emailType={emailType}
          scenarioName={title}
        />
      ) : null}
    </>
  );
};
